import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
    getTableColumnPreferences,
    resetTableColumnPreference,
    saveTableColumnPreference,
    TableColumnPreferenceId,
    type TableColumnPreference,
    type TableColumnPreferenceIdValue,
} from '@/services/tableColumnPreferenceService';

type Preferences = Partial<Record<TableColumnPreferenceIdValue, TableColumnPreference>>;

export const useTableColumnPreferencesStore = defineStore('tableColumnPreferences', () => {
    const preferences = ref<Preferences>({});
    const loadedUserId = ref<number | null>(null);
    const loading = ref(false);
    const saving = ref(false);
    const resetting = ref(false);
    let loadSequence = 0;
    let loadingUserId: number | null = null;

    const payments = computed(() => preferences.value[TableColumnPreferenceId.PAYMENTS] ?? null);

    async function load(userId: number): Promise<void> {
        if (loadedUserId.value === userId || (loading.value && loadingUserId === userId)) return;

        loadSequence += 1;
        const sequence = loadSequence;
        loadingUserId = userId;
        preferences.value = {};
        loadedUserId.value = null;
        loading.value = true;

        try {
            const loadedPreferences = await getTableColumnPreferences();
            if (sequence !== loadSequence) return;
            preferences.value = loadedPreferences;
        } catch (error) {
            if (sequence !== loadSequence) return;
            // Loading preferences is optional: the table must remain usable with system defaults.
            console.warn('Failed to load table column preferences:', error);
            preferences.value = {};
        } finally {
            if (sequence === loadSequence) {
                loadedUserId.value = userId;
                loading.value = false;
                loadingUserId = null;
            }
        }
    }

    async function save(
        tableId: TableColumnPreferenceIdValue,
        preference: TableColumnPreference,
    ): Promise<void> {
        const sequence = loadSequence;
        saving.value = true;
        try {
            const saved = await saveTableColumnPreference(tableId, preference);
            if (sequence === loadSequence) {
                preferences.value = { ...preferences.value, [tableId]: saved };
            }
        } finally {
            if (sequence === loadSequence) saving.value = false;
        }
    }

    async function reset(tableId: TableColumnPreferenceIdValue): Promise<void> {
        const sequence = loadSequence;
        resetting.value = true;
        try {
            await resetTableColumnPreference(tableId);
            if (sequence === loadSequence) {
                const next = { ...preferences.value };
                delete next[tableId];
                preferences.value = next;
            }
        } finally {
            if (sequence === loadSequence) resetting.value = false;
        }
    }

    function clear(): void {
        loadSequence += 1;
        loadingUserId = null;
        preferences.value = {};
        loadedUserId.value = null;
        loading.value = false;
        saving.value = false;
        resetting.value = false;
    }

    return {
        preferences,
        loadedUserId,
        loading,
        saving,
        resetting,
        payments,
        load,
        save,
        reset,
        clear,
    };
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useTableColumnPreferencesStore, module.hot));
}
